/**
 * Dominios de correo autorizados para entrar a una compañía.
 * La lista vive en Companies/{companyId}.allowedEmailDomains y la edita el admin
 * desde Configuración › Usuarios. Lista vacía o ausente = compañía abierta.
 */

const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/

/**
 * Dominios del equipo de la plataforma. Entran a cualquier compañía sin importar
 * su lista y viven en código a propósito: si fueran configurables, el admin de una
 * compañía podría quitarlos y dejar al equipo fuera de su propia cuenta.
 */
export const INTERNAL_EMAIL_DOMAINS = ['kila.co', 'kontroll.tech'] as const

/** Acepta 'kila.co', '@kila.co' o 'juan@kila.co' y devuelve 'kila.co'. null si no es un dominio. */
export function normalizeDomain(raw: string): string | null {
  const value = raw.trim().toLowerCase().replace(/^@/, '')
  const domain = value.includes('@') ? value.split('@').pop() || '' : value
  return DOMAIN_PATTERN.test(domain) ? domain : null
}

export function emailDomain(email: string): string {
  return email.trim().toLowerCase().split('@').pop() || ''
}

export function isInternalEmail(email: string): boolean {
  return INTERNAL_EMAIL_DOMAINS.includes(emailDomain(email) as typeof INTERNAL_EMAIL_DOMAINS[number])
}

/**
 * Match contra la lista de la compañía, sin excepciones. El match es exacto:
 * 'kila.co' no autoriza 'mail.kila.co'. Si la compañía usa subdominios, el admin
 * los agrega como entradas aparte.
 */
export function isEmailDomainAllowed(email: string, domains?: string[] | null): boolean {
  if (!domains?.length) return true
  const domain = emailDomain(email)
  return domains.some((allowed) => normalizeDomain(allowed) === domain)
}

/**
 * La puerta de entrada a una compañía: cumplir su lista, o ser del equipo con el
 * correo ya verificado.
 *
 * Lo de verificado no es un detalle. Firebase crea la cuenta de correo y contraseña
 * sin comprobar que el correo sea tuyo, así que sin esa condición cualquiera con un
 * enlace de invitación se registraría como algo@kila.co y entraría a la compañía
 * saltándose su lista. En la práctica el bypass queda para Google/Microsoft, que sí
 * comprueban el dominio.
 */
export function canEmailJoinCompany(
  email: string,
  domains: string[] | null | undefined,
  emailVerified: boolean
): boolean {
  return isEmailDomainAllowed(email, domains) || (emailVerified && isInternalEmail(email))
}

/** Se lanza donde un correo intentaría entrar a una compañía que no lo permite. */
export class EmailDomainNotAllowedError extends Error {
  readonly code = 'auth/domain-not-allowed'

  constructor(readonly allowedDomains: string[] = []) {
    super('auth/domain-not-allowed')
    this.name = 'EmailDomainNotAllowedError'
  }
}
